<script>
import { onMounted, ref, watch, inject, computed } from 'vue'
import { useRouter } from 'vue-router'
import { useVuelidate } from '@vuelidate/core'
import { required, maxLength } from '@vuelidate/validators'
import { XCircleIcon } from '@heroicons/vue/20/solid'
import { useRestoGLobalStore } from '@/stores/restoGlobalStore'
import { useCartGLobalStore } from '@/stores/cartGlobalStore'
import { confirmDetailsFn, setOrderOptionsFn } from '@/services/api/restApiCalls'
import MollieCardGateway from './MollieCardGateway.vue'
import LoadingSpinner from '@/components/common/LoadingSpinner/LoadingSpinner.vue'
import { notify } from 'notiwind'

export default {
  components: {
    MollieCardGateway,
    LoadingSpinner,
    XCircleIcon
  },
  setup() {
    const IS_MOBILE_APP = inject('IS_MOBILE_APP')
    const router = useRouter()
    const restoStore = useRestoGLobalStore()
    const cartStore = useCartGLobalStore()
    const phoneNumber = ref('')
    const noteForResto = ref('')
    const submitted = ref(false)
    const formError = ref('')
    const handleCardFn = ref(null)
    const gatewayPayButtonDisabled = ref(true)
    const isLoading = ref(false)
    const v$ = useVuelidate({ phoneNumber: { required, maxLength: maxLength(15) } }, { phoneNumber })

    const buildOrderOptionFormData = () => {
      const orderOptionFormData = new FormData()
      orderOptionFormData.append('resto_id', restoStore?.RESTO_ID)
      orderOptionFormData.append('requested_order_dt', cartStore.CART_DATA?.requested_order_dt)
      orderOptionFormData.append('order_note', noteForResto?.value)
      if (IS_MOBILE_APP) {
        orderOptionFormData.append('is_mobile_app', 1)
      }
      return orderOptionFormData
    }

    const buildCollectionConfirmFormData = () => {
      const collectionOrderFormData = new FormData()
      collectionOrderFormData.append('resto_id', restoStore?.RESTO_ID)
      collectionOrderFormData.append('phone_number', phoneNumber.value)
      return collectionOrderFormData
    }

    const runOrderPreflight = async () => {
      let order_option = await setOrderOptionsFn(buildOrderOptionFormData())
      if (order_option?.status != 200) {
        order_option = await setOrderOptionsFn(buildOrderOptionFormData())
      }
      if (order_option?.status != 200) {
        router.push({ name: 'menu', params: { menu_slug: restoStore?.MENU_URL } })
        return false
      }
      const confirm_detail = await confirmDetailsFn(buildCollectionConfirmFormData())
      if (confirm_detail?.status == 200) {
        return true
      }
      formError.value = confirm_detail?.status_msg
      cartStore.SHOW_POSTCODE_MODAL = true
      notify({ group: 'errormessage', title: 'Error', text: `${formError.value}` }, 2000)
      return false
    }

    const onGatewayPayButtonState = (disabled) => {
      gatewayPayButtonDisabled.value = disabled !== false
    }

    const isCardPaymentButtonDisabled = computed(() => gatewayPayButtonDisabled.value)

    const handleCollectionOrder = async () => {
      formError.value = ''
      submitted.value = true
      v$.value.$touch()
      if (v$.value.$invalid || isLoading.value) return

      isLoading.value = true
      try {
        const ok = await runOrderPreflight()
        if (!ok) {
          isLoading.value = false
          return
        }
        handleCardFn.value?.handleCardPayment()
      } catch (error) {
        console.error('Order Error:', error)
      }
      isLoading.value = false
    }

    onMounted(() => {
      phoneNumber.value = restoStore?.CUSTOMER_INFO?.phone_number || ''
      noteForResto.value = cartStore?.CART_DATA.order_note || ''
    })
    watch([phoneNumber, noteForResto], () => {
      formError.value = ''
    })
    watch(
      () => handleCardFn.value?.payButtonDisabled,
      (newVal, oldVal) => {
        if (newVal !== oldVal) isLoading.value = false
      }
    )

    return {
      phoneNumber,
      noteForResto,
      submitted,
      formError,
      handleCollectionOrder,
      v$,
      restoStore,
      handleCardFn,
      onGatewayPayButtonState,
      isLoading,
      syncOrderBeforeCharge: runOrderPreflight,
      isCardPaymentButtonDisabled
    }
  }
}
</script>
<template>
  <div>
    <label for="phoneNumber" class="block text-sm font-medium text-gray-900 dark:text-gray-300">Phone Number</label>
    <div class="mt-1">
      <input
        id="phoneNumber"
        v-model="phoneNumber"
        type="text"
        :class="{ 'border-red-600': submitted && v$.phoneNumber.$error }"
        class="block w-full appearance-none rounded-md border border-gray-300 dark:border-gray-600 px-3 py-2 placeholder-gray-400 dark:placeholder-gray-500 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-indigo-500 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-200"
      />
    </div>
    <div v-for="(error, index) of v$.phoneNumber.$errors" :key="index" class="input-errors">
      <div class="text-sm font-medium text-red-600 dark:text-red-400">{{ error.$message }}</div>
    </div>
  </div>
  <div>
    <label for="comment" class="block text-sm font-medium text-gray-900 dark:text-gray-300">Note For Restaurant</label>
    <div class="mt-1">
      <textarea
        id="comment"
        v-model="noteForResto"
        rows="4"
        placeholder="Add instruction about food or mention any allergy"
        class="block w-full rounded-md border border-gray-300 dark:border-gray-600 px-3 py-2 placeholder-gray-400 dark:placeholder-gray-500 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-indigo-500 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-200"
      ></textarea>
    </div>
  </div>
  <MollieCardGateway
    ref="handleCardFn"
    :before-place-order="syncOrderBeforeCharge"
    @pay-button-state="onGatewayPayButtonState"
  />
  <div v-if="formError" class="rounded-md bg-red-50 dark:bg-red-900/20 px-4 py-2">
    <div class="flex justify-center items-center">
      <div class="flex-shrink-0">
        <XCircleIcon class="lg:h-8 lg:w-8 h-6 w-6 text-red-400 dark:text-red-500" aria-hidden="true" />
      </div>
      <div class="ml-2">
        <p class="lg:text-md text-sm lg:font-medium text-red-800 dark:text-red-400">{{ formError }}</p>
      </div>
    </div>
  </div>
  <button
    type="submit"
    class="w-full justify-center rounded-md border border-transparent bg-violet-700 py-2 px-4 text-sm font-medium text-white shadow-sm hover:bg-violet-800 focus:outline-none focus:ring-2 focus:ring-violet-500 focus:ring-offset-2 disabled:bg-gray-300 disabled:text-gray-400 dark:disabled:bg-gray-700 dark:disabled:text-gray-500"
    @click.prevent.stop="handleCollectionOrder()"
    :disabled="isLoading || isCardPaymentButtonDisabled"
  >
    Confirm Payment
  </button>
  <button
    type="submit"
    class="w-full rounded-md border border-transparent bg-gray-400 py-2 px-4 text-sm font-medium text-white shadow-sm hover:bg-gray-400 focus:outline-none focus:ring-offset-gray-50 dark:bg-gray-600 dark:hover:bg-gray-700"
    @click="$router.push({ name: 'menu', params: { menu_slug: restoStore?.MENU_URL } })"
  >
    Change Order
  </button>
  <LoadingSpinner :isLoading="isLoading" />
</template>
